import type { MaterialOption } from './MaterialAutocomplete.types';
import type {
    EmptySubcategoryPlaceholder,
    NonValueOption,
    ValueType,
} from './utils';
import type {
    ExtendedMaterial,
    ExtendedMaterialOnTheFly,
    Material,
    MaterialOnTheFly,
} from '../../gql';

import { isExtendedMaterialOnTheFly, isMaterialOnTheFly } from '../../utils';

import { fillerMaterialOnTheFly } from './utils';

/**
 * This array with undefined is for when the options are loaded.
 * They are appended to the options, and each undefined is rendered as a skeleton.
 */
const loadingOptions = Array.from({ length: 20 }).map(() => undefined);

interface BuildMaterialOptionsArgs {
    /** Whether a backend search is currently in flight */
    isSearching: boolean;
    /** Whether a freshly typed query is still waiting for matching results */
    isSearchPending: boolean;
    /** Whether the user is actively searching */
    isSearchActive: boolean;
    /** Whether the loading skeletons should be shown */
    isLoading: boolean;
    /** Whether the add MOF button should be included */
    isCustomAllowed: boolean;
    /** Whether the options are grouped by their material subcategory */
    isGroupedByMaterialSubcategory: boolean;
    /** The raw materials provided to the component */
    materials: Array<MaterialOption>;
    /** The regular material options, pre-sorted by subcategory name */
    materialOptions: Array<ExtendedMaterial | Material>;
    /** The material on the fly options (or the filler when there are none) */
    materialOnTheFlyOptions: Array<ExtendedMaterialOnTheFly | MaterialOnTheFly>;
    /** The expected subcategory names in browse mode, in stable order */
    materialSubcategoryNames: Array<string> | undefined;
    /** The subcategory group names that are currently expanded */
    expandedGroups: Set<string>;
    /** The non-value option that renders the add MOF button */
    addMofOption: NonValueOption;
}

/**
 * @description Builds the full, ordered list of options passed to the autocomplete.
 * It combines the add MOF button, material on the fly options, the (grouped or flat)
 * material options, empty subcategory placeholders, and loading skeletons depending on
 * the current search and loading state.
 * @returns The ordered list of options to render in the dropdown
 */
const buildMaterialOptions = ({
    isSearching,
    isSearchPending,
    isSearchActive,
    isLoading,
    isCustomAllowed,
    isGroupedByMaterialSubcategory,
    materials,
    materialOptions,
    materialOnTheFlyOptions,
    materialSubcategoryNames,
    expandedGroups,
    addMofOption,
}: BuildMaterialOptionsArgs): Array<ValueType> => {
    // While searching (or while a freshly typed query is still pending), we only
    // show the loading state so we never flash a list built from stale options.
    if (isSearching || isSearchPending) {
        const searchLoadingOptions = [undefined];

        return [
            ...(isCustomAllowed ? [addMofOption] : []),
            ...searchLoadingOptions,
        ];
    }

    const hasActualMaterials = materials.some((material) => {
        if (
            isMaterialOnTheFly(material) ||
            isExtendedMaterialOnTheFly(material)
        ) {
            return (
                material.materialOnTheFlyPublicId !==
                fillerMaterialOnTheFly.materialOnTheFlyPublicId
            );
        }
        return true;
    });

    if (!hasActualMaterials) {
        return [
            ...(isCustomAllowed ? [addMofOption] : []),
            ...(isLoading ? loadingOptions : []),
        ];
    }

    /**
     * When grouping by material subcategory, we check if any expected subcategories are missing from the results.
     * For each missing subcategory, we add a placeholder option so that the group header still renders,
     * and the group component shows "No materials found" for that subcategory.
     * In browse mode, the provider supplies per-group data so we can show empty placeholders as soon as
     * the count-only query returns (totalCount===0). We detect this by checking which subcategory names
     * from subcategoryIdByName are absent from the current materials list.
     */
    const emptySubcategoryPlaceholders: Array<EmptySubcategoryPlaceholder> = [];
    if (isGroupedByMaterialSubcategory && materialSubcategoryNames) {
        const subcategoriesWithMaterials = new Set(
            materialOptions.map(
                (material) =>
                    material.materialSubcategory.materialSubcategoryName
            )
        );
        materialSubcategoryNames.forEach((name) => {
            if (!subcategoriesWithMaterials.has(name)) {
                emptySubcategoryPlaceholders.push({
                    emptySubcategoryPlaceholderId: `empty-subcategory-${name}`,
                    materialSubcategoryName: name,
                });
            }
        });
    }

    /**
     * When grouping by subcategory, collapsed groups hide their materials.
     * For each collapsed subcategory (that has materials), we replace its materials
     * with a single collapsed placeholder so the group header still renders while the
     * materials themselves stay hidden. While searching, all groups are forced open so
     * matching results remain visible. The materials are pre-sorted by subcategory, so
     * emitting one placeholder at the first occurrence keeps the group order intact.
     *
     * In browse mode (isGroupedByMaterialSubcategory && !isSearchActive) we never add
     * global loading skeletons at the bottom — per-group sentinels handle pagination.
     */
    const groupedMaterialOptions: Array<ValueType> = [];
    if (isGroupedByMaterialSubcategory && !isSearchActive) {
        const collapsedSubcategories = new Set<string>();
        materialOptions.forEach((material) => {
            const subcategoryName =
                material.materialSubcategory.materialSubcategoryName;
            if (expandedGroups.has(subcategoryName)) {
                groupedMaterialOptions.push(material);
                return;
            }
            if (!collapsedSubcategories.has(subcategoryName)) {
                collapsedSubcategories.add(subcategoryName);
                groupedMaterialOptions.push({
                    collapsedSubcategoryPlaceholderId: `collapsed-subcategory-${subcategoryName}`,
                    materialSubcategoryName: subcategoryName,
                });
            }
        });
    } else if (
        isGroupedByMaterialSubcategory &&
        isSearchActive &&
        materialSubcategoryNames
    ) {
        /**
         * Search mode with grouping: interleave materials and empty placeholders
         * in the stable order provided by materialSubcategoryNames (from the metadata
         * provider). This preserves the initial browse order regardless of which
         * groups have search matches.
         */
        const materialsBySubcategory = new Map<string, Array<ValueType>>();
        materialOptions.forEach((material) => {
            const name = material.materialSubcategory.materialSubcategoryName;
            const group = materialsBySubcategory.get(name);
            if (group) {
                group.push(material);
            } else {
                materialsBySubcategory.set(name, [material]);
            }
        });
        materialSubcategoryNames.forEach((name) => {
            const group = materialsBySubcategory.get(name);
            if (group) {
                groupedMaterialOptions.push(...group);
            }
        });
    } else {
        groupedMaterialOptions.push(...materialOptions);
    }

    return [
        ...(isCustomAllowed ? [addMofOption] : []),
        ...materialOnTheFlyOptions,
        ...groupedMaterialOptions,
        ...(isGroupedByMaterialSubcategory && isSearchActive
            ? []
            : emptySubcategoryPlaceholders),
        // In browse mode, no global loading skeletons. In search/flat mode, keep them.
        ...(!isGroupedByMaterialSubcategory || isSearchActive
            ? isLoading
                ? loadingOptions
                : []
            : []),
    ];
};

export { buildMaterialOptions };
